// CanopyGen (gs-canopygen; APP_FORESTVOL §3, contracts C1 / C3 / C8 / C10):
// ONE GenPass of gensched.js that generates, once per tile, the CANOPY TILE
// of the measured crowns into the canopy pool — the far forest's height,
// silhouette, per-tree look and baked light. Nothing draws it yet (the far
// volume, gs-forestvol, consumes canopyTextures / canopySlot).
//
// Per tile (level c: 256² texels of 2·2^c m, the work frame = the tile + a
// HALO, crowns.js), raw WebGL2 (no float-render extension), in tick time
// (gensched genFrame, never inside a render), three's state reset after:
//   1. ground  the resident ground-tree nodes (their drawn meshes, coarse to
//              fine, each overwriting) → depth Dg (DEPTH32F, 1 − rel / SPAN)
//   2. splat   Dg blitted into Ds; one instanced quad per crown (every crown
//              pack the tile + REACH covers), standing on Dg at its centre,
//              its profile written as gl_FragDepth with the depth test LESS
//              (the max height wins; ties: the earlier crown) + the winner's
//              G-buffer (RGBA32UI: hash, species | crown flag, the crown-
//              local normal snorm16, crown depth unorm16)
//   3. resolve H level 0 (R16UI, 1/32 m above y0, 0 = no canopy)
//   4. h mips  the MAX-MIPMAP chain (each level the max of its 2×2)
//   5. shade   A (sRGB: albedo × E / W, coverage) + B (sky-vis, sun-vis,
//              snow-catch, species) — the bake's light model (meta.tex.light,
//              meta.lighting), the season's species colour (C10, the tile's
//              doy; the species' own ramps from the world's SEA1, opt.sea),
//              the per-crown position hash
//   6. ab mips coverage-weighted means; species of the tallest child
// Mips are rendered into scratch 2D textures (the level below read through
// TEXTURE_BASE_LEVEL: no feedback loop) and, in the same pass, into the pool
// layer (MRT) — never generateMipmap on the array. MIP-PINGPONG: level l of a
// scratch chain lives in Sx (l even) / Sx1 (l odd), so a mip pass never samples
// a texture attached to its framebuffer — Chrome's validating decoder (Android /
// Quest) checks every sampler against every attachment, levels ignored, and
// DROPS the draw (INVALID_OPERATION "Source and destination textures of the
// draw are the same"; ANGLE checks levels and drew it). crowns.js is the CPU
// reference of every step (check-forest; __vr.genCheck diffs a GPU tile).
//
// Terrain light inputs (the light's only inputs from outside the crowns):
// SHD2 (meta.surface.shade ver 2, no other ver) vis_opaque = the sun-vis
// sampler, sky_opaque = a second one multiplying the crown openness (E's sky
// term and B.r), both the level whose cell = the canopy texel: the frame maps
// texel-exactly onto it, FRAME_N² u8 per channel composed from the ≤ 4 tiles
// it overlaps (indices clamped to the level), uploaded at the shade stage.
// setSunVisSource(fn) overrides: fn(frame) → {tex, xf, sky?: {tex, xf}}.
//
// Inputs: the crown packs + the SHD2 tiles, through two decoded LRUs (gensched
// InputCache: packs PACK_LRU, SHD2 tiles SHD_LRU, never evicting an input of a
// queued tile): inputs(tile) asks only for what they lack; the queue shares a
// url another queued tile fetched (one fetch per input per need).
// The CPU prep is a generator (a pack decode, the gather, the splat's instance
// arrays every STEP_RECORDS records, the SHD2 frame grids every STEP_ROWS rows,
// the WebP decodes async — their Promise while pending: the job parks,
// gensched THE PARKED WAIT) run within the ms the queue gives: a piece starts
// only while its estimate fits what is left (the run's first always), 'more'
// else (gs-canopygen-slice); the stages' uploads (a ground geometry, the splat
// instances UPLOAD_CROWNS at a time) are pieces of their own ahead of the
// stage; the GPU stages run while the frame's
// estimated GPU ms (the median measured per stage) fits the ms the queue gave
// at the frame's first run, the frame's first stage always ('yield' else).
//
// Tiles: a quadtree from canopy.level_max down to level_min — a level-c tile
// splits nearer than SPLIT_K × its texel — within vol.range_m, not wholly
// inside the hand-off band's start N − F (handoff.js: nearer, the near
// sprites own it; every band point has its tile — the volume's half of the
// band and the sprites' light there), only where a crown pack
// reaches; rank = distance (nearest first). The pool (C3: A / B / H
// 2D arrays, gensched canopyLayerBytes a layer) is allocated at the first
// run (after heavyOk), as many layers as are wanted then (≥ 8), and grows
// (doubling, to canopy.layers) when the wanted tiles outnumber it.
//
// TWO FAMILIES (gs-family-shrub): the pass is instantiated per family
// descriptor (FAM below over crowns.js FAMILY: id = GenPass + pool, the pack
// index + kind, the level texel / tile, the ground node of a level, the live
// keys — layers, levels, range, the near cut —, the crown profile GLSL, the
// colour rule + light numbers). FAM.tree is the canopy above, unchanged;
// FAM.shrub the shrub crown packs (meta.crowns.shrubs, CPK1 kind 1) at 1 / 2 m
// texels into its OWN pool ('shrub'): tiles from shrubs.view_m − live
// shrub.fade_m (the near shrubs own the inside) to the live shrub.range_m,
// level 0 near, 1 beyond SPLIT_K × 2 m; a dome per shrub record (the bake's
// shrub caster), the shrub style colour, no season. Nothing draws the shrub
// tiles yet (shrubtap.js is the ground tree's tap, wired in S2).
import * as THREE from 'three';
import RENDER_CFG from './renderconfig.json';
import { gen, onGenChange, genLive, canopyLayerBytes, CANOPY_TEXELS, InputCache } from './gensched.js';
import { transportUrl } from './assetq.js';
import { run as poolRun } from './pool.js';
import { CPK_H_PER_M } from './crownpack.js';
import { sunDirFromLighting } from './scenelight.js';
import { resolveDoy, readSea } from './season.js';
import { HANDOFF, handoffBand } from './handoff.js';
import {
  CANOPY_N, HALO, FRAME_N, CANOPY_MIPS, SPAN_M, H_Q, AO_DIRS, AO_EXCESS, INNER_AO, VAR_K, VAR_WARM, SILHOUETTE,
  SHRUB_TOP_M, SHRUB_R, SHRUB_PROFILE, FAMILY,
  speciesCtx, seasonSpecies, canopyFrame, packsFor, gatherCrownsSteps, decodeCrownsSteps, aoTaps, canopyRef,
  shrubCtx, shrubStyles, shrubFrame, gatherShrubsSteps, shrubRef, familyTiles,
  SUNVIS_ONE, frameVis,
} from './crowns.js';

const POOL_MIN = 8;
const WANT_MS = 1000, WANT_M = 64;
const PACK_LRU = 48, SHD_LRU = 32;  // decoded crown packs / SHD2 tiles kept
// the GPU chain in STAGES, as many a frame as their estimated GPU ms fit
// (gensched C8 'yield' else): 0 ground, 1 splat + resolve + max-mips,
// 2 shade + A / B mips
const STAGES = ['ground', 'splat', 'shade'];
const ONE_XF = [0, 0, 0.5, 0.5];    // the 1×1 white: visibility 1
const NOTHING_YET = 'nothing generated yet';
const HIST_N = 501;
// the prep's step sizes (gs-canopygen-slice: every piece of a run well under the
// phones' / Quest's 1–2 ms gen slice): records a pack-decode / instance step, SHD2 frame rows a step
const STEP_RECORDS = 4096, STEP_ROWS = 16;
const PIECE_DECAY = 0.9;
const UPLOAD_CROWNS = 16384;        // splat instances a bufferSubData piece (24 B each)                 // the run histogram: 0.1 ms bins, the last = ≥ 50 ms

if (CANOPY_N !== CANOPY_TEXELS) throw new Error('canopy: crowns.js CANOPY_N != gensched CANOPY_TEXELS');

// ---------------------------------------------------------------- GLSL
const HDR = `#version 300 es
precision highp float; precision highp int; precision highp sampler2D; precision highp usampler2D;
`;
const VS_FULL = `${HDR}void main() { vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2)); gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0); }`;

const VS_GROUND = `${HDR}
layout(location = 0) in vec3 aPos;
uniform vec4 uFr;      // frame x0, z0, extent m, y0
uniform float uSpan;
void main() {
  vec2 p = (aPos.xz - uFr.xy) / uFr.z * 2.0 - 1.0;
  gl_Position = vec4(p, (1.0 - (aPos.y - uFr.w) / uSpan) * 2.0 - 1.0, 1.0);
}`;
const FS_NONE = `${HDR}void main() {}`;

// the family's crown: crowns.js crownGeom (trees) / shrubGeom (shrubs), line for line
const f1 = (v) => (Number.isInteger(v) ? v.toFixed(1) : String(v));
const GEOM_TREE = `  uint sp = aI.y; vec4 S = uSpA[sp];
  float s = aC.z / 8.0 * S.x;
  if (aC.w == 0.0) s *= 0.45 + 0.55 * clamp((uFr2.z - yb) / 140.0, 0.0, 1.0);
  float wf = aC.w > 0.0 ? clamp(2.35 * (aC.w * 0.2) / max(1.0, s), 0.28, 1.15) : S.y;
  float R = 0.5 * ${SILHOUETTE.toFixed(4)} * s * wf * uSpRk[sp];
  vG = vec4(aC.xy, R, yb + s); vG2 = vec2(yb + s * S.z, S.w); vI = aI;
`;
const GEOM_SHRUB = `  float s = min(aC.z / ${f1(CPK_H_PER_M[1])}, ${f1(SHRUB_TOP_M)});
  float R = yb > uFr2.z ? 0.0 : ${SHRUB_R.toFixed(4)} * s;
  vG = vec4(aC.xy, R, yb + s); vG2 = vec2(yb + s * ${SHRUB_PROFILE[1].toFixed(4)}, 1.0); vI = aI;
`;
// crowns.js crownGeom / shrubGeom + splatRef, line for line
const vsSplat = (zName, geom) => `${HDR}
layout(location = 0) in vec4 aC;     // frame-local x, z (m), h byte, r byte
layout(location = 1) in uvec2 aI;    // position hash, species
uniform sampler2D uDg;
uniform vec4 uFr2;                   // texel m, frame texels, ${zName}, span
uniform vec4 uSpA[4];                // hScale, w, crown base, dome
uniform float uSpRk[4];              // the bare radius factor
flat out vec4 vG;                    // cx, cz, R, top
flat out vec2 vG2;                   // base, dome
flat out uvec2 vI;
void main() {
  float t = uFr2.x; int n = int(uFr2.y);
  ivec2 c = clamp(ivec2(floor(aC.xy / t)), ivec2(0), ivec2(n - 1));
  float yb = (1.0 - texelFetch(uDg, c, 0).r) * uFr2.w;
${geom}  vec2 corner = vec2(float(gl_VertexID & 1), float(gl_VertexID >> 1)) * 2.0 - 1.0;
  float ext = float(n) * t;
  gl_Position = R > 0.0 ? vec4((aC.xy + corner * (R + 1e-3)) / ext * 2.0 - 1.0, 0.0, 1.0) : vec4(2.0, 2.0, 2.0, 1.0);
}`;
const FS_SPLAT = `${HDR}
flat in vec4 vG; flat in vec2 vG2; flat in uvec2 vI;
uniform vec4 uFr2;
layout(location = 0) out uvec4 oG;
void main() {
  vec2 p = gl_FragCoord.xy * uFr2.x;
  float dx = p.x - vG.x, dz = p.y - vG.y;
  float rho = length(vec2(dx, dz)), q = rho / vG.z;
  if (q >= 1.0) discard;
  float a = vG.w - vG2.x, sq = sqrt(1.0 - q * q);
  bool dome = vG2.y > 0.5;
  float zc = dome ? 0.5 * (vG.w + vG2.x) + 0.5 * a * sq : vG.w - a * q;
  float sl = dome ? (0.5 * a * q) / (vG.z * max(sq, 0.05)) : a / vG.z;
  vec2 u = rho > 1e-6 ? sl * vec2(dx, dz) / rho : vec2(0.0);
  vec3 nn = normalize(vec3(u.x, 1.0, u.y));
  gl_FragDepth = 1.0 - zc / uFr2.w;
  float rel = a > 1e-3 ? (vG.w - zc) / a : 0.0;
  oG = uvec4(vI.x, vI.y | 16u, packSnorm2x16(nn.xz), uint(floor(clamp(rel, 0.0, 1.0) * 65535.0 + 0.5)));
}`;

const FS_RESOLVE = `${HDR}
uniform usampler2D uG; uniform sampler2D uDs; uniform float uSpan;
layout(location = 0) out uint o0; layout(location = 1) out uint o1;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy) + ${HALO};
  uint h = 0u;
  if ((texelFetch(uG, p, 0).y & 16u) != 0u) {
    float z = (1.0 - texelFetch(uDs, p, 0).r) * uSpan;
    h = uint(clamp(floor(z * ${H_Q.toFixed(1)} + 0.5), 1.0, 65535.0));
  }
  o0 = h; o1 = h;
}`;

const FS_HMIP = `${HDR}
uniform usampler2D uSrc;
layout(location = 0) out uint o0; layout(location = 1) out uint o1;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy) * 2;
  uint h = max(max(texelFetch(uSrc, p, 0).r, texelFetch(uSrc, p + ivec2(1, 0), 0).r),
               max(texelFetch(uSrc, p + ivec2(0, 1), 0).r, texelFetch(uSrc, p + ivec2(1, 1), 0).r));
  o0 = h; o1 = h;
}`;

// crowns.js shadeRef, line for line (P: the family's light, crowns.js TREE_LIGHT / SHRUB_LIGHT)
const fsShade = (P) => `${HDR}
uniform usampler2D uG; uniform sampler2D uDs; uniform sampler2D uSv; uniform sampler2D uSk;
uniform vec4 uSvXf, uSkXf;           // frame m -> sun- / sky-vis uv: xy * xf.xy + xf.zw
uniform vec4 uFr2;                   // texel m, frame texels, -, span
uniform vec3 uSky, uGnd, uSun, uL;   // meta.tex.light, toward the sun
uniform float uWhite;
uniform vec3 uSunStep;               // the sun's horizontal unit (x, z), tan(alt)
uniform vec3 uCol[4];                // species colour (linear, season)
uniform vec2 uSpB[4];                // opacity, snow catch
uniform vec2 uAo[${2 * AO_DIRS}];    // openness taps (m)
uniform float uAoR[2];
layout(location = 0) out vec4 oA0; layout(location = 1) out vec4 oB0;
layout(location = 2) out vec4 oA1; layout(location = 3) out vec4 oB1;
int n; float t;
int at(vec2 q) {
  ivec2 i = ivec2(floor(q / t));
  return (i.x < 0 || i.y < 0 || i.x >= n || i.y >= n) ? -1 : 0;
}
void main() {
  t = uFr2.x; n = int(uFr2.y); float span = uFr2.w;
  ivec2 p = ivec2(gl_FragCoord.xy) + ${HALO};
  vec2 pp = (vec2(p) + 0.5) * t;
  float tsv = texture(uSv, pp * uSvXf.xy + uSvXf.zw).r;
  float tsky = texture(uSk, pp * uSkXf.xy + uSkXf.zw).r;
  uvec4 g = texelFetch(uG, p, 0);
  vec4 A = vec4(0.0), B = vec4(tsky, tsv, 0.0, 1.0);
  if ((g.y & 16u) != 0u) {
    float z = (1.0 - texelFetch(uDs, p, 0).r) * span;
    uint sp = g.y & 15u;
    vec2 nxz = unpackSnorm2x16(g.z);
    float ny = sqrt(max(0.0, 1.0 - nxz.x * nxz.x - nxz.y * nxz.y));
    float rel = float(g.w) / 65535.0;
    float vis = 1.0;
    for (int k = 1; k <= ${P.sunSteps}; k++) {
      float s = float(k) * ${P.selfM.toFixed(1)} / ${P.sunSteps.toFixed(1)};
      vec2 q = pp + s * uSunStep.xy;
      if (at(q) < 0) continue;
      ivec2 qi = ivec2(floor(q / t));
      uvec4 gq = texelFetch(uG, qi, 0);
      if ((gq.y & 16u) != 0u && (1.0 - texelFetch(uDs, qi, 0).r) * span > z + s * uSunStep.z + ${P.sunEps.toFixed(3)}) {
        vis = 1.0 - uSpB[gq.y & 15u].x; break;
      }
    }
    float open = 0.0;
    for (int k = 0; k < ${2 * AO_DIRS}; k++) {
      vec2 q = pp + uAo[k];
      float e = 0.0;
      if (at(q) >= 0) e = max(0.0, (1.0 - texelFetch(uDs, ivec2(floor(q / t)), 0).r) * span - z) * ${AO_EXCESS.toFixed(3)} / uAoR[k / ${AO_DIRS}];
      open += 1.0 / (1.0 + e * e);
    }
    float sky = (open / ${(2 * AO_DIRS).toFixed(1)}) * (1.0 - ${INNER_AO.toFixed(3)} * rel) * tsky;
    vec3 nrm = vec3(nxz.x, ny, nxz.y);
    float nl = max(0.0, dot(nrm, uL));
    float w = 0.5 * ny + 0.5, sun = nl * vis * tsv;
    float u1 = float((g.x >> 8u) & 255u) / 255.0, u2 = float((g.x >> 16u) & 255u) / 255.0;
    float kk = ${VAR_K[0].toFixed(3)} + ${VAR_K[1].toFixed(3)} * u1, warm = (u2 - 0.5) * ${VAR_WARM.toFixed(3)};
    vec3 vk = vec3(kk * (1.0 + warm), kk, kk * (1.0 - warm));
    vec3 E = sky * (uGnd + (uSky - uGnd) * w) + uSun * sun;
    A = vec4(clamp(clamp(uCol[sp] * vk, 0.0, 1.0) * E / uWhite, 0.0, 1.0), 1.0);
    float ck = uSpB[sp].y;
    float sm = clamp((ny - 0.3) / 0.6, 0.0, 1.0); sm = sm * sm * (3.0 - 2.0 * sm);
    B = vec4(sky, vis * tsv, clamp(ck * sm * (1.0 - ${P.catchDepth.toFixed(1)} * rel), 0.0, 1.0), float(sp) / 255.0);
  }
  oA0 = A; oB0 = B; oA1 = A; oB1 = B;
}`;

const FS_ABMIP = `${HDR}
uniform sampler2D uA; uniform sampler2D uB; uniform usampler2D uH;
layout(location = 0) out vec4 oA0; layout(location = 1) out vec4 oB0;
layout(location = 2) out vec4 oA1; layout(location = 3) out vec4 oB1;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy) * 2;
  vec3 ca = vec3(0.0), cb = vec3(0.0), mb = vec3(0.0); float sa = 0.0, spc = 1.0; uint hm = 0u;
  for (int k = 0; k < 4; k++) {
    ivec2 q = p + ivec2(k & 1, k >> 1);
    vec4 a = texelFetch(uA, q, 0), b = texelFetch(uB, q, 0); uint h = texelFetch(uH, q, 0).r;
    ca += a.rgb * a.a; cb += b.rgb * a.a; mb += b.rgb; sa += a.a;
    if (h > hm) { hm = h; spc = b.a; }
  }
  vec4 A = sa > 0.0 ? vec4(ca / sa, sa * 0.25) : vec4(0.0);
  vec4 B = vec4(sa > 0.0 ? cb / sa : mb * 0.25, spc);
  oA0 = A; oB0 = B; oA1 = A; oB1 = B;
}`;

// a depth-and-G read-back for __vr.genCheck: the float bits of Dg / Ds
const FS_PEEK = `${HDR}
uniform sampler2D uDg; uniform sampler2D uDs;
layout(location = 0) out uvec4 o;
void main() { ivec2 p = ivec2(gl_FragCoord.xy); o = uvec4(floatBitsToUint(texelFetch(uDg, p, 0).r), floatBitsToUint(texelFetch(uDs, p, 0).r), 0u, 0u); }`;

// a family's programs (fam: FAM.tree | FAM.shrub)
const programs = (fam) => ({
  ground: [VS_GROUND, FS_NONE], splat: [vsSplat(fam.zName, fam.geomGLSL), FS_SPLAT], resolve: [VS_FULL, FS_RESOLVE], hmip: [VS_FULL, FS_HMIP],
  shade: [VS_FULL, fsShade(fam.light)], abmip: [VS_FULL, FS_ABMIP], peek: [VS_FULL, FS_PEEK],
});

// ---------------------------------------------------------------- the families
// crowns.js FAMILY + what the pass reads per family: the pack index, the
// model (ctx), the frame, the per-"species" uniforms, the gather, the CPU
// reference, the regen params, the live keys → {cap, lo, hi, range, vm}
const FAM = {
  tree: {
    ...FAMILY.tree, tag: 'canopy', zName: 'treeline rel', geomGLSL: GEOM_TREE, params: ['season.date'],
    index: (meta) => meta.crowns,
    ctx: (meta, sea) => speciesCtx(meta, readSea(sea)),   // the world's SEA1 (main.js boot): required
    frame: (c, i, j, y0, ctx) => canopyFrame(c, i, j, y0, ctx ? ctx.treeline : undefined),
    frameZ: (F) => F.treelineRel,
    uniforms: (ctx, doy) => seasonSpecies(ctx, doy),
    gather: gatherCrownsSteps,
    ref: (st, ground, ctx, light, L, sv, sk) => canopyRef(st.decoded, ground, st.frame, ctx, st.doy, light, L, sv, sk),
    // the band's start: forestvol's live view and the Forest's cap (handoff.js), else the live view's
    live: (live) => {
      if (!live.canopy) return null;
      const lo = Math.max(0, Math.min(FAMILY.tree.levels - 1, live.canopy.level_min | 0));
      const hi = Math.max(lo, Math.min(FAMILY.tree.levels - 1, live.canopy.level_max | 0));
      const sp = live.sprites || {};
      const vm = HANDOFF.uNear.value > 0 ? handoffBand()[0] : Math.max(0, (sp.view_m || 0) - (sp.fade_m || 0));
      return { cap: Math.max(0, live.canopy.layers | 0), lo, hi, range: (live.vol && live.vol.range_m) || 0, vm };
    },
  },
  shrub: {
    ...FAMILY.shrub, tag: 'canopy shrub', zName: 'shrub cap rel', geomGLSL: GEOM_SHRUB, params: [],
    index: (meta) => meta.crowns && meta.crowns.shrubs,
    ctx: (meta) => shrubCtx(meta),
    frame: (c, i, j, y0, ctx) => shrubFrame(c, i, j, y0, ctx ? ctx.cap : undefined),
    frameZ: (F) => F.capRel,
    uniforms: (ctx) => shrubStyles(ctx),
    gather: gatherShrubsSteps,
    ref: (st, ground, ctx, light, L, sv, sk) => shrubRef(st.decoded, ground, st.frame, ctx, light, L, sv, sk),
    // N = renderconfig shrubs.view_m (the near shrubs), F = the live shrub.fade_m:
    // the tiles from N − F out to the live shrub.range_m, levels 0..1
    live: (live) => {
      const s = live.shrub;
      if (!s) return null;
      const view = (RENDER_CFG.shrubs && RENDER_CFG.shrubs.view_m) || 0;
      return { cap: Math.max(0, s.layers | 0), lo: 0, hi: FAMILY.shrub.levels - 1, range: s.range_m || 0,
        vm: Math.max(0, view - Math.max(0, s.fade_m || 0)) };
    },
  },
};
// the GPU stages' frame accounting, shared by the families (one queue, one
// frame): the queue's ms at the frame's first run, the GPU ms issued, stages
const FRAME = { fr: -1, ms: 0, gpu: 0, n: 0 };

// ---------------------------------------------------------------- the layer
class CanopyGen {
  constructor({ renderer, meta, sea, baseUrl, tree }, fam = FAM.tree) {
    const tag = fam.tag;
    const lt = meta.tex && meta.tex.light;
    if (!(lt && lt.white > 0)) throw new Error(`${tag}: world meta.tex.light.white missing`);
    const L = sunDirFromLighting(meta.lighting);
    if (!L) throw new Error(`${tag}: world meta.lighting missing`);
    // the terrain light inputs: SHD2 only (no other ver, no compat), a vis +
    // a sky level at every canopy texel
    const sh = meta.surface && meta.surface.shade;
    if (!sh || sh.ver !== 2 || !sh.shd2) throw new Error(`${tag}: meta.surface.shade ver ${sh ? sh.ver : 'absent'} (reads ver 2, SHD2)`);
    if (!(sh.shd2.tile_px > 0)) throw new Error(`${tag}: meta.surface.shade.shd2.tile_px missing`);
    this.fam = fam;
    this.id = fam.id;
    this.tag = tag;
    this.shd2 = sh.shd2;
    this.shdLv = Array.from({ length: fam.levels }, (_, c) => Object.fromEntries(['vis', 'sky'].map((ch) => {
      const lv = (sh.shd2[ch] || []).find((v) => Math.abs(v.cell_m - fam.texel(c)) < 1e-9);
      if (!lv) throw new Error(`${tag}: SHD2 has no ${ch} level at ${fam.texel(c)} m`);
      return [ch, lv];
    })));
    this.renderer = renderer;
    this.meta = meta;
    this.baseUrl = baseUrl;
    this.tree = tree;               // groundtree.js GroundTree: index (y ranges) + the resident nodes
    this.crowns = fam.index(meta);
    this.light = lt;
    this.L = L;
    this.ctx = fam.ctx(meta, sea);
    this.world = meta.bbox ? { w: meta.bbox.e1 - meta.bbox.e0, h: meta.bbox.n1 - meta.bbox.n0 } : null;
    this.sunVisFor = null;          // null = SHD2; setSunVisSource overrides
    this.gl = null;                 // GL objects (_glInit at the first run)
    this.poolTex = null;            // {A, B, H} three DataArrayTextures, layers
    this.info = [];                 // per pool layer: the tile it holds
    this.packCache = new Map();     // tile key -> packsFor
    this.packLru = new InputCache(PACK_LRU);   // pack url -> decodeCrowns (pinned by the queued tiles)
    this.shdLru = new InputCache(SHD_LRU);     // SHD2 tile url -> Uint8Array tile_px² grey (the same)
    this._asked = new Map();        // tile id -> { urls inputs() asked for (job.inputs' order), held: the LRU hits then }
    this._wantAt = -Infinity; this._wantPos = null; this._dirty = true; this._wanted = 0;
    this.broken = null;
    this.stats = { tiles: 0, crowns: 0, nodes: 0, cpu_ms: [], run_hist: new Uint32Array(HIST_N), run_max: 0, gap_max: 0, gap_at: 0, step_max: {}, gpu_ms: STAGES.map(() => []), gpu_tile_ms: [], grow: 0, alloc_ms: 0, compile_ms: 0 };
    this._q = [];                   // pending GPU timer queries {q, stage, seq}
    this._qSum = new Map();         // seq → {ms, n}: a tile's stages summed
    this._pieceEst = {};            // label → the estimated CPU ms of its next piece (_piece)
    this._inPieces = 0;             // the current run's ms inside its pieces (_piece; _cpu's gap)
    this._at = 'setup';             // the label of the prep's next piece (_prepSteps sets it)
    this._seq = 0; this._epoch = 0; // a tile's GPU sequence; a scratch / pool change restarts the stages
    const ID = this.id;
    gen.pool(ID, 0, canopyLayerBytes());
    this.pass = {
      id: ID, params: fam.params, pool: ID,
      key: (t) => t.id,
      need: (t) => (this.broken ? Infinity : t.rank),
      // a tile with no content yet is a hole in the forest: it leads beside the ground's near loads (in play)
      fill: (t) => gen.slotOf(ID, t.id) < 0,
      // the CPU prep vs a GPU stage (a few GL calls): each run kind banks to its own learned floor
      step: (job) => (job.state.prep ? 'gpu' : ''),
      inputs: (t) => this._ask(t),
      // the ground under the tile is not drawn yet: NOT READY (waiting, not a failure; gensched ready)
      ready: (t) => this._groundUnder(this.fam.frame(t.c, t.i, t.j, 0)),
      run: (gl, job, ms) => this._run(gl, job, ms),
      // THE PASS INIT (gensched, while hidden): the programs issued (linked: without the
      // parallel extension LINK_STATUS waits here, behind the black), the GL objects, the pool storage
      init: (gl) => {
        if (this.broken || !gl) return;
        try {
          if (!this.gl) this._glInit(gl);
          this._compiled(gl);
          const P = gen.pools.get(this.id);
          // a 0-layer pool (registered at construction, nothing wanted yet) has no GL side: a texStorage3D
          // of depth 0 fails and leaves incomplete textures bound (the consumers keep their 1×1 stand-ins)
          if (P && P.layers > 0 && (!this.poolTex || this.poolTex.layers !== P.layers)) this._poolAlloc(P.layers);
        } finally { this.renderer.resetState(); }
      },
      ref: (tile, inputs) => this._ref(tile, inputs),
      done: (job) => { this.info[job.target.layer] = job.state.prep.info; this.stats.tiles++; this._asked.delete(job.tile.id); },
      evict: (id, layer) => { if (this.info[layer] && this.info[layer].id === id) this.info[layer] = null; },
    };
    gen.register(this.pass);
    onGenChange((kinds) => { if (kinds.has('pool') || kinds.has('need') || kinds.has('uniform')) this._dirty = true; });
  }

  // ---- the tiles wanted (own cadence: WANT_MS or WANT_M moved, or a live change)
  tick(pos, now) {
    const live = genLive(), ix = this.tree && this.tree.index, ID = this.id;
    const v = live && this.fam.live(live);
    if (!v || !ix || !pos) return;
    const moved = !this._wantPos || Math.hypot(pos.x - this._wantPos.x, pos.z - this._wantPos.z) > WANT_M;
    if (!this._dirty && !moved && now - this._wantAt < WANT_MS) return;
    this._dirty = false; this._wantAt = now; this._wantPos = { x: pos.x, z: pos.z };
    const { cap, lo, hi, range, vm } = v;
    const tiles = cap > 0 && range > 0 ? this._select(pos, lo, hi, range, vm, ix) : [];
    gen.want(ID, tiles, pos);
    this._wanted = tiles.length;
    // an ask is kept while the queue holds its tile (a fetch in flight / inputs held for a later want)
    const Q = gen.passes.get(ID).tiles;
    for (const id of this._asked.keys()) if (!Q.has(id)) this._asked.delete(id);
    // the decoded inputs of every queued (wanted, not fresh) tile are pinned: never evicted before its prep
    const pin = [];
    for (const t of tiles) if (gen.stateOf(ID, t.id) !== 'fresh') pin.push(...flatUrls(this._urls(t)));
    this.packLru.pin(pin.filter(isPack)); this.shdLru.pin(pin.filter((u) => !isPack(u)));
    // the pool: what is wanted now (≥ POOL_MIN), doubling while it outgrows it, ≤ cap
    const P = gen.pools.get(ID), have = P ? P.layers : 0;
    let want = have;
    if (!have) want = Math.min(cap, Math.max(POOL_MIN, tiles.length));
    else if (tiles.length > have && have < cap) want = Math.min(cap, Math.max(2 * have, tiles.length));
    else if (have > cap) want = cap;
    if (want !== have && tiles.length) { gen.pool(ID, want, canopyLayerBytes()); this.info = []; if (have) this.stats.grow++; }
  }

  // crowns.js familyTiles (vm: the hand-off band's start N − F — a tile
  // whose farthest point is inside it is the near sprites' / shrubs', so is
  // every child: not wanted); a tile's packs cached by its id
  _select(pos, lo, hi, range, vm, ix) {
    const fam = this.fam;
    return familyTiles(fam, pos, { lo, hi, range, vm,
      hasNode: (c, i, j) => ix.nodes.has(`${fam.node(c)}_${i}_${j}`),
      packsOf: (c, i, j) => {
        const id = `${c}_${i}_${j}`;
        let packs = this.packCache.get(id);
        if (!packs) this.packCache.set(id, packs = packsFor(fam.frame(c, i, j, 0), this.crowns, fam.reach));
        return packs;
      } });
  }

  // the tile's height floor: the ground index's subtree minimum over the
  // node and its 8 neighbours (the halo reaches into them), − 1 m
  _yRange(c, i, j) {
    const ns = this.tree.index.nodes, k = this.fam.node(c);
    let y0 = Infinity, y1 = -Infinity;
    for (let b = -1; b <= 1; b++) for (let a = -1; a <= 1; a++) {
      const n = ns.get(`${k}_${i + a}_${j + b}`);
      if (n) { y0 = Math.min(y0, n.Y0 ?? n.y0); y1 = Math.max(y1, n.Y1 ?? n.y1); }
    }
    if (!Number.isFinite(y0)) throw new Error(`${this.tag} ${c}_${i}_${j}: no ground index node`);
    y0 = Math.floor(y0) - 1;
    if (y1 + 40 - y0 > SPAN_M) console.warn(`${this.tag} ${c}_${i}_${j}: relief ${Math.round(y1 - y0)} m past the ${SPAN_M} m span (clamped)`);
    return [y0, y1 + 40];
  }

  // ---- the inputs: every url a tile reads — its crown packs, the SHD2 vis /
  // sky tiles under its frame ([gx, gz, url])
  _urls(tile) {
    const F = this.fam.frame(tile.c, tile.i, tile.j, 0), s2 = this.shd2, out = {
      packs: tile.packs.map(([i, j]) => `${this.baseUrl}/${this.crowns.dir}/${i}_${j}.cpk`) };
    for (const ch of ['vis', 'sky']) {
      const lv = this.shdLv[tile.c][ch];
      out[ch] = shdSpan(lv, F, s2.tile_px).map(([gx, gz]) => [gx, gz, `${this.baseUrl}/${s2.dir}/${ch}/${cellG(lv.cell_m)}/${gx}_${gz}.${s2.ext}`]);
    }
    return out;
  }
  // pass.inputs: the urls the LRUs lack now (remembered: job.inputs' order);
  // the hits are HELD for the prep (other tiles' decodes may evict them first)
  _ask(tile) {
    const urls = [], held = new Map();
    for (const u of flatUrls(this._urls(tile))) {
      const v = lruOf(this, u).get(u);
      if (v === undefined) urls.push(u); else held.set(u, v);
    }
    this._asked.set(tile.id, { urls, held });
    return urls;
  }

  // a tile input left the LRU between inputs() and the prep: the queue
  // entry forgets its inputs (the retry, RETRY_MS on, asks again)
  _lost(tile, own, what) {
    this._asked.delete(tile.id);
    if (own) gen.reask(this.id, tile.id);
    throw new Error(`${this.tag} ${tile.id}: ${what} (re-asked at the retry)`);
  }

  // ---- CPU prep (a generator: yields between steps, a pending Promise while
  // the SHD2 decodes run): the frame, the crowns, the species / season
  // uniforms, the SHD2 frame grids. bufs = the fetched `asked` urls; the rest
  // comes from the LRUs. own: a queue job (_lost re-asks)
  *_prepSteps(tile, bufs, asked, own, held) {
    this._at = 'setup';
    const [y0, y1] = this._yRange(tile.c, tile.i, tile.j);
    const frame = this.fam.frame(tile.c, tile.i, tile.j, y0, this.ctx);
    if (!asked) { if (bufs.length) this._lost(tile, own, 'no asked urls'); asked = []; }
    if (asked.length !== bufs.length) this._lost(tile, own, `${bufs.length} inputs for ${asked.length} urls`);
    const U = this._urls(tile), mine = new Set(asked), got = new Map();
    // the LRU hits first (before this tile's own inserts can evict them)
    for (const u of flatUrls(U)) {
      if (mine.has(u)) continue;
      const v = (held && held.get(u)) || lruOf(this, u).get(u);
      if (v === undefined) this._lost(tile, own, `${u} left the LRU`);
      got.set(u, v);
    }
    // the SHD2 tiles decode async (all at once), the packs one a step
    const tp = this.shd2.tile_px, pend = [];
    asked.forEach((u, k) => {
      if (!isPack(u)) pend.push(decodeGrey(bufs[k], tp).then((a) => { this.shdLru.put(u, a); got.set(u, a); }));
    });
    let fin = false, err = null;
    const all = Promise.all(pend).then(() => { fin = true; }, (e) => { err = e; });
    yield;
    for (let k = 0; k < asked.length; k++) {
      if (!isPack(asked[k])) continue;
      this._at = 'decode';
      const d = yield* decodeCrownsSteps(bufs[k], STEP_RECORDS);
      this.packLru.put(asked[k], d); got.set(asked[k], d);
      yield;
    }
    const decoded = U.packs.map((u) => got.get(u));
    const live = genLive();
    const doy = resolveDoy(live && live.season ? live.season.date : '09-25');
    const spc = this.fam.uniforms(this.ctx, doy);
    this._at = 'gather';
    const C = yield* this.fam.gather(decoded, frame, this.ctx);
    // the splat's instance arrays (stage 1 uploads them as they are)
    this._at = 'inst';
    const inst = yield* instArrays(C);
    this._at = 'shd';
    while (!fin && !err) yield all;
    if (err) throw err;
    const shd = {};
    for (const ch of ['vis', 'sky']) shd[ch] = yield* shdFrameSteps(this.shdLv[tile.c][ch], frame, tp, new Map(U[ch].map(([gx, gz, u]) => [`${gx}_${gz}`, got.get(u)])));
    return { frame, decoded, doy, spc, C, inst, shd, info: { id: tile.id, c: tile.c, i: tile.i, j: tile.j, X0: frame.X0, Z0: frame.Z0,
      tile_m: frame.tileM, texel_m: frame.t, y0, y1, doy } };
  }

  // the prep drained outside the queue (check, ref): bufs = every _urls url
  async _prep(tile, bufs) {
    const g = this._prepSteps(tile, bufs, flatUrls(this._urls(tile)), false);
    for (;;) {
      const r = g.next();
      if (r.done) return r.value;
      if (r.value) await r.value;
    }
  }

  // gates: inputs = the buffers of every _urls url (flatUrls order) → a Promise
  async _ref(tile, inputs) {
    const st = await this._prep(tile, inputs);
    if (!tile.ground) throw new Error(`${this.tag} ref: tile.ground (the frame ground, rel m) required`);
    return this.fam.ref(st, tile.ground, this.ctx, this.light, this.L, frameVis(st.shd.vis, st.frame), frameVis(st.shd.sky, st.frame));
  }

  // the estimated GPU ms of a stage: the median measured (0 unmeasured)
  _est(k) {
    const a = this.stats.gpu_ms[k];
    return a.length ? [...a].sort((x, y) => x - y)[a.length >> 1] : 0;
  }

  // ---- the GenPass run: prep (the generator, within ms; 'more' past it,
  // its pending decodes' Promise: the job parks, gensched) → compile wait → the GPU chain, a
  // STAGE while the frame's estimated GPU ms fit the ms the queue gave at the
  // frame's first run (its first stage always), 'yield' else; a scratch /
  // pool change in between (check, a pool resize) restarts at stage 0
  _run(gl, job, ms) {
    if (!gl) throw new Error(`${this.tag}: no WebGL2 context`);
    const t0 = performance.now(), st = job.state;
    this._inPieces = 0;
    const fr = this.renderer.info.render.frame;
    if (fr !== FRAME.fr) { FRAME.fr = fr; FRAME.ms = ms; FRAME.gpu = 0; FRAME.n = 0; }
    // (timed as pieces, so a run's gap names them: the first all-complete _compiled asks LINK_STATUS, a sync round trip)
    const tc = performance.now();
    if (!this.gl) this._glInit(gl);
    const linked = this._compiled(gl);
    this._piece('compile', tc);
    if (!linked) return 'yield';
    // a piece (a prep step, a stage's CPU) starts only while its estimate fits the ms left — the
    // run's first always (its size is what gensched learns and banks): a run ≤ max(ms, one piece)
    let pieces = 0;
    const fits = (label) => pieces === 0 || performance.now() - t0 + (this._pieceEst[label] || 0) <= ms;
    if (!st.prep) {
      const a = this._asked.get(job.tile.id);
      if (!st.steps) { this._at = 'setup'; st.steps = this._prepSteps(job.tile, job.inputs, a && a.urls, true, a && a.held); }
      for (;;) {
        if (!fits(this._at)) { this._cpu(t0); return 'more'; }
        const tp = performance.now();
        const r = st.steps.next();
        this._piece(this._at, tp); pieces++;
        if (r.done) { st.prep = r.value; break; }
        if (r.value) { this._cpu(t0); return r.value; }   // the SHD2 decodes pending: THE PARKED WAIT (gensched)
      }
    }
    const P = gen.pools.get(this.id);
    if (!this.poolTex || this.poolTex.layers !== P.layers) { const ta = performance.now(); this._poolAlloc(P.layers); this._piece('alloc', ta); }
    if (st.epoch !== this._epoch) { st.epoch = this._epoch; st.stage = 0; st.seq = ++this._seq; }
    // the stages' uploads as pieces of their own, ahead of them (a ground geometry each, the splat's
    // instances UPLOAD_CROWNS a piece); a stage uploads whatever is still missing itself (same bytes)
    for (let u; (u = this._upNext(st)); ) {
      if (!fits(u)) { this._cpu(t0); return 'more'; }
      const tu = performance.now();
      try { this._upload(gl, st, u); } finally { this.renderer.resetState(); }
      this._piece(u, tu); pieces++;
    }
    const est = this._est(st.stage);
    if (FRAME.n > 0 && FRAME.gpu + est > FRAME.ms) { this._cpu(t0); return 'yield'; }
    if (!fits(STAGES[st.stage])) { this._cpu(t0); return 'more'; }
    FRAME.gpu += est; FRAME.n++;
    const tg = performance.now();
    try {
      this._gpu(gl, st.prep, job.target.layer, st.stage, st.seq);
    } finally {
      this.renderer.resetState();
    }
    this._piece(STAGES[st.stage], tg);
    this._cpu(t0);
    return ++st.stage < STAGES.length ? 'more' : 'done';
  }

  // the next upload piece of the job's coming stages: 'up.geo' (stage 0: a drawn ground node under the
  // frame without its GL buffers), 'up.inst' (stages 0–1: the splat instances not in bufC / bufI yet), null
  _upNext(st) {
    if (st.stage === 0 && this._geoTodo(st.prep.frame)) return 'up.geo';
    if (st.stage <= 1 && st.prep.C.n && (this.gl.instOf !== st.prep || this.gl.instDone < st.prep.C.n)) return 'up.inst';
    return null;
  }
  _geoTodo(F) {
    for (const nd of this._groundNodes(F)) if (nd.mesh.geometry.index && !this.gl.geo.has(nd.mesh.geometry)) return nd.mesh.geometry;
    return null;
  }
  _upload(gl, st, u) {
    if (u === 'up.geo') { const g = this._geoTodo(st.prep.frame); if (g) this._geoGL(gl, g); gl.bindVertexArray(null); }
    else this._instUp(gl, st.prep, UPLOAD_CROWNS);
  }
  // the splat's instance arrays of prep S into bufC / bufI, the next `max` crowns (G.instOf = whose
  // they are: another tile's upload in between — a check — starts over)
  _instUp(gl, S, max) {
    const G = this.gl, n = S.C.n, { fc, ic } = S.inst;
    if (G.instOf !== S) {
      G.instOf = S; G.instDone = 0;
      gl.bindBuffer(gl.ARRAY_BUFFER, G.bufC); gl.bufferData(gl.ARRAY_BUFFER, fc.byteLength, gl.STREAM_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, G.bufI); gl.bufferData(gl.ARRAY_BUFFER, ic.byteLength, gl.STREAM_DRAW);
    }
    const k0 = G.instDone, k1 = Math.min(n, k0 + max);
    if (k1 > k0) {
      gl.bindBuffer(gl.ARRAY_BUFFER, G.bufC); gl.bufferSubData(gl.ARRAY_BUFFER, 16 * k0, fc, 4 * k0, 4 * (k1 - k0));
      gl.bindBuffer(gl.ARRAY_BUFFER, G.bufI); gl.bufferSubData(gl.ARRAY_BUFFER, 8 * k0, ic, 2 * k0, 2 * (k1 - k0));
    }
    G.instDone = k1;
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
  }

  // a run's CPU: the window (cpu_ms) + the whole-session histogram (0.1 ms bins, run_hist)
  _cpu(t0) {
    const ms = performance.now() - t0;
    push(this.stats.cpu_ms, ms);
    this.stats.run_hist[Math.min(HIST_N - 1, Math.floor(ms * 10))]++;
    if (ms > this.stats.run_max) this.stats.run_max = ms;
    // the run's CPU outside its pieces (a GC, a sync GL call): the largest, with when (performance.now s)
    const g = ms - this._inPieces;
    if (g > this.stats.gap_max) { this.stats.gap_max = g; this.stats.gap_at = Math.round(t0 / 100) / 10; }
  }
  // one indivisible piece of a run (a prep step / a GPU stage's CPU): its largest per label (step_max)
  // and its estimate (_pieceEst: the largest recent, decaying PIECE_DECAY a piece)
  _piece(label, t) {
    const ms = performance.now() - t, m = this.stats.step_max, E = this._pieceEst;
    this._inPieces += ms;
    if (!(ms <= m[label])) m[label] = ms;
    E[label] = Math.max(ms, (E[label] || 0) * PIECE_DECAY);
  }

  // ---- GL objects
  _glInit(gl) {
    const t0 = performance.now();
    const G = { progs: {}, loc: {}, par: gl.getExtension('KHR_parallel_shader_compile'),
      timer: gl.getExtension('EXT_disjoint_timer_query_webgl2') };
    for (const [k, [vs, fs]] of Object.entries(programs(this.fam))) {
      const p = gl.createProgram();
      for (const [type, src] of [[gl.VERTEX_SHADER, vs], [gl.FRAGMENT_SHADER, fs]]) {
        const s = gl.createShader(type);
        gl.shaderSource(s, src); gl.compileShader(s); gl.attachShader(p, s);
      }
      gl.linkProgram(p);
      G.progs[k] = p;
    }
    const tex2 = (fmt, w, levels, filter) => {
      const t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texStorage2D(gl.TEXTURE_2D, levels, fmt, w, w);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return t;
    };
    G.Dg = tex2(gl.DEPTH_COMPONENT32F, FRAME_N, 1, gl.NEAREST);
    G.Ds = tex2(gl.DEPTH_COMPONENT32F, FRAME_N, 1, gl.NEAREST);
    G.Gb = tex2(gl.RGBA32UI, FRAME_N, 1, gl.NEAREST);
    G.Sh = tex2(gl.R16UI, CANOPY_N, CANOPY_MIPS, gl.NEAREST);
    G.Sa = tex2(gl.SRGB8_ALPHA8, CANOPY_N, CANOPY_MIPS, gl.NEAREST);
    G.Sb = tex2(gl.RGBA8, CANOPY_N, CANOPY_MIPS, gl.NEAREST);
    // the odd mip levels' twins (MIP-PINGPONG, _sc)
    G.Sh1 = tex2(gl.R16UI, CANOPY_N, CANOPY_MIPS, gl.NEAREST);
    G.Sa1 = tex2(gl.SRGB8_ALPHA8, CANOPY_N, CANOPY_MIPS, gl.NEAREST);
    G.Sb1 = tex2(gl.RGBA8, CANOPY_N, CANOPY_MIPS, gl.NEAREST);
    G.one = tex2(gl.R8, 1, 1, gl.LINEAR);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 1, 1, gl.RED, gl.UNSIGNED_BYTE, new Uint8Array([255]));
    G.Tv = tex2(gl.R8, FRAME_N, 1, gl.LINEAR);   // the frame's SHD2 vis / sky (shade stage)
    G.Tk = tex2(gl.R8, FRAME_N, 1, gl.LINEAR);
    gl.bindTexture(gl.TEXTURE_2D, null);
    G.fbG = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, G.fbG);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, G.Dg, 0);
    gl.drawBuffers([gl.NONE]);
    G.fbS = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, G.fbS);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, G.Gb, 0);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, G.Ds, 0);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
    G.fbO = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    G.vaoGround = gl.createVertexArray();
    G.vaoSplat = gl.createVertexArray();
    G.vaoFull = gl.createVertexArray();
    G.bufC = gl.createBuffer(); G.bufI = gl.createBuffer();
    gl.bindVertexArray(G.vaoSplat);
    gl.bindBuffer(gl.ARRAY_BUFFER, G.bufC);
    gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 4, gl.FLOAT, false, 0, 0); gl.vertexAttribDivisor(0, 1);
    gl.bindBuffer(gl.ARRAY_BUFFER, G.bufI);
    gl.enableVertexAttribArray(1); gl.vertexAttribIPointer(1, 2, gl.UNSIGNED_INT, 0, 0); gl.vertexAttribDivisor(1, 1);
    gl.bindVertexArray(G.vaoGround);
    gl.enableVertexAttribArray(0);
    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    G.geo = new WeakMap();          // ground geometry -> {vbo, ibo, count, type}
    this.gl = G;
    this.renderer.resetState();
    this.stats.compile_ms += performance.now() - t0;
  }

  // every program linked (KHR_parallel_shader_compile: no stall while it runs)
  _compiled(gl) {
    const G = this.gl;
    if (G.linked) return true;
    if (G.par) for (const p of Object.values(G.progs)) if (!gl.getProgramParameter(p, G.par.COMPLETION_STATUS_KHR)) return false;
    for (const [k, p] of Object.entries(G.progs)) {
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        const sh = gl.getAttachedShaders(p).map((s) => gl.getShaderInfoLog(s)).filter(Boolean).join(' | ');
        this.broken = `program ${k}: ${gl.getProgramInfoLog(p)} ${sh}`.trim();
        console.error(`${this.tag}: ${this.broken}`);
        throw new Error(this.broken);
      }
      G.loc[k] = new Map();
    }
    G.linked = true;
    return true;
  }

  _u(gl, prog, name) {
    const m = this.gl.loc[prog];
    if (!m.has(name)) m.set(name, gl.getUniformLocation(this.gl.progs[prog], name));
    return m.get(name);
  }

  // the pool: three DataArrayTextures (C3), storage only (texStorage3D of the
  // whole chain through renderer.initTexture, the GmTex pattern), filled by
  // raw GL here; consumers bind them as three textures
  _poolAlloc(layers) {
    const t0 = performance.now();
    this._epoch++;   // a staged job's pool H is gone: it restarts
    if (this.poolTex) for (const k of ['A', 'B', 'H']) this.poolTex[k].dispose();
    const mk = (fmt, type, cs, filter, internal) => {
      const t = new THREE.DataArrayTexture(null, CANOPY_N, CANOPY_N, layers);
      t.format = fmt; t.type = type; t.colorSpace = cs;
      if (internal) t.internalFormat = internal;
      t.minFilter = filter[0]; t.magFilter = filter[1];
      t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
      t.generateMipmaps = false;
      t.mipmaps = Array.from({ length: CANOPY_MIPS }, (_, l) => ({ data: null, width: CANOPY_N >> l, height: CANOPY_N >> l, depth: layers }));
      t.source.dataReady = false;
      t.needsUpdate = true;
      this.renderer.initTexture(t);
      return t;
    };
    const lin = [THREE.LinearMipmapLinearFilter, THREE.LinearFilter], near = [THREE.NearestMipmapNearestFilter, THREE.NearestFilter];
    const T = {
      A: mk(THREE.RGBAFormat, THREE.UnsignedByteType, THREE.SRGBColorSpace, lin),
      B: mk(THREE.RGBAFormat, THREE.UnsignedByteType, THREE.NoColorSpace, lin),
      H: mk(THREE.RedIntegerFormat, THREE.UnsignedShortType, THREE.NoColorSpace, near, 'R16UI'),
      layers,
    };
    for (const k of ['A', 'B', 'H']) T[`gl${k}`] = this.renderer.properties.get(T[k]).__webglTexture;
    this.poolTex = T;
    this.info = [];
    this.stats.alloc_ms += performance.now() - t0;
  }

  // a ground geometry's GL buffers (its arrays uploaded once, freed with it)
  _geoGL(gl, geo) {
    let g = this.gl.geo.get(geo);
    if (g) return g;
    const pos = geo.attributes.position.array, idx = geo.index && geo.index.array;
    if (!idx) return null;
    g = { vbo: gl.createBuffer(), ibo: gl.createBuffer(), count: idx.length, type: idx instanceof Uint32Array ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT };
    gl.bindBuffer(gl.ARRAY_BUFFER, g.vbo); gl.bufferData(gl.ARRAY_BUFFER, pos, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    this.gl.geo.set(geo, g);
    // the element buffer is VAO state: uploaded under the ground VAO
    gl.bindVertexArray(this.gl.vaoGround);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, g.ibo); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
    geo.addEventListener('dispose', () => { gl.deleteBuffer(g.vbo); gl.deleteBuffer(g.ibo); this.gl.geo.delete(geo); });
    return g;
  }

  // the drawn ground nodes under a frame, coarse first (a finer one overwrites)
  _groundNodes(frame) {
    const ext = frame.n * frame.t, out = [];
    for (const n of this.tree.nodes.values()) {
      if (!n.mesh || n.k < this.fam.groundMinK(frame.c)) continue;
      const s = 128 * 2 ** n.k, x = n.tx * s, z = n.ty * s;
      if (x >= frame.x0 + ext || x + s <= frame.x0 || z >= frame.z0 + ext || z + s <= frame.z0) continue;
      out.push(n);
    }
    return out.sort((a, b) => b.k - a.k);
  }

  // the tile's ground must be drawn: a 4×4 probe grid, every point under a resident node
  _groundUnder(F, nodes = this._groundNodes(F)) {
    for (let b = 0; b < 4; b++) for (let a = 0; a < 4; a++) {
      const x = F.X0 + (a + 0.5) * F.tileM / 4, z = F.Z0 + (b + 0.5) * F.tileM / 4;
      if (!nodes.some((nd) => { const s = 128 * 2 ** nd.k; return x >= nd.tx * s && x < (nd.tx + 1) * s && z >= nd.ty * s && z < (nd.ty + 1) * s; })) return false;
    }
    return true;
  }

  // ---- THE GPU chain of one tile into pool layer `layer`
  // stage 0 / 1 / 2 (STAGES), or -1 = all (check: untimed)
  _gpu(gl, S, layer, stage = -1, seq = 0) {
    const G = this.gl, T = this.poolTex, F = S.frame, n = F.n, t = F.t, ext = n * t;
    const all = stage < 0;
    let q = null;
    if (!all && G.timer && !gl.getQuery(G.timer.TIME_ELAPSED_EXT, gl.CURRENT_QUERY)) { q = gl.createQuery(); gl.beginQuery(G.timer.TIME_ELAPSED_EXT, q); }
    gl.disable(gl.BLEND); gl.disable(gl.CULL_FACE); gl.disable(gl.SCISSOR_TEST); gl.disable(gl.STENCIL_TEST);
    gl.disable(gl.SAMPLE_ALPHA_TO_COVERAGE); gl.disable(gl.POLYGON_OFFSET_FILL);
    gl.colorMask(true, true, true, true);
    if (all || stage === 0) {
    const nodes = this._groundNodes(F);
    // the pass's ready() holds a tile back until its ground is drawn: absent here, it left mid-run
    if (!this._groundUnder(F, nodes)) {
      if (q) { gl.endQuery(G.timer.TIME_ELAPSED_EXT); gl.deleteQuery(q); }
      throw new Error(`${this.tag} ${S.info.id}: the ground under it is not resident`);
    }
    this.stats.nodes += nodes.length;
    // 1. ground → Dg
    gl.bindFramebuffer(gl.FRAMEBUFFER, G.fbG);
    gl.viewport(0, 0, n, n);
    gl.enable(gl.DEPTH_TEST); gl.depthMask(true); gl.clearDepth(1); gl.clear(gl.DEPTH_BUFFER_BIT);
    gl.depthFunc(gl.ALWAYS);
    gl.useProgram(G.progs.ground);
    gl.uniform4f(this._u(gl, 'ground', 'uFr'), F.x0, F.z0, ext, F.y0);
    gl.uniform1f(this._u(gl, 'ground', 'uSpan'), SPAN_M);
    gl.bindVertexArray(G.vaoGround);
    for (const nd of nodes) {
      const g = this._geoGL(gl, nd.mesh.geometry);
      if (!g) continue;
      gl.bindBuffer(gl.ARRAY_BUFFER, g.vbo);
      gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, g.ibo);
      gl.drawElements(gl.TRIANGLES, g.count, g.type, 0);
    }
    }
    if (all || stage === 1) {
    // 2. splat: Dg → Ds, crowns over it. A stage runs frames after the one
    // before it, three's draws between: it sets every state it needs (an
    // inherited viewport put the splat off the frame — every staged tile
    // resident with H = 0, the volume's out-of-steps lid painted black)
    gl.viewport(0, 0, n, n);
    gl.enable(gl.DEPTH_TEST); gl.depthMask(true);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, G.fbG);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, G.fbS);
    gl.blitFramebuffer(0, 0, n, n, 0, 0, n, n, gl.DEPTH_BUFFER_BIT, gl.NEAREST);
    gl.bindFramebuffer(gl.FRAMEBUFFER, G.fbS);
    gl.clearBufferuiv(gl.COLOR, 0, new Uint32Array(4));
    gl.depthFunc(gl.LESS);
    const C = S.C;
    if (C.n) {
      this._instUp(gl, S, Infinity);   // what the upload pieces left (all of it: a check, the one-call path)
      gl.useProgram(G.progs.splat);
      this._tex(gl, 0, gl.TEXTURE_2D, G.Dg);
      gl.uniform1i(this._u(gl, 'splat', 'uDg'), 0);
      gl.uniform4f(this._u(gl, 'splat', 'uFr2'), t, n, this.fam.frameZ(F), SPAN_M);
      const spA = new Float32Array(16), rk = new Float32Array(4);
      S.spc.forEach((s, k) => { spA.set([s.hScale, s.w, s.base, s.dome], 4 * k); rk[k] = s.rk; });
      gl.uniform4fv(this._u(gl, 'splat', 'uSpA'), spA);
      gl.uniform1fv(this._u(gl, 'splat', 'uSpRk'), rk);
      gl.bindVertexArray(G.vaoSplat);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, C.n);
    }
    this.stats.crowns += C.n;
    gl.disable(gl.DEPTH_TEST); gl.depthMask(false);
    gl.bindVertexArray(G.vaoFull);
    // 3. resolve → Sh0 + pool H0
    this._out(gl, [[G.Sh, 0], [T.glH, 0, layer]], CANOPY_N);
    gl.useProgram(G.progs.resolve);
    this._tex(gl, 0, gl.TEXTURE_2D, G.Gb); gl.uniform1i(this._u(gl, 'resolve', 'uG'), 0);
    this._tex(gl, 1, gl.TEXTURE_2D, G.Ds); gl.uniform1i(this._u(gl, 'resolve', 'uDs'), 1);
    gl.uniform1f(this._u(gl, 'resolve', 'uSpan'), SPAN_M);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    // 4. the max-mip chain
    gl.useProgram(G.progs.hmip);
    gl.uniform1i(this._u(gl, 'hmip', 'uSrc'), 0);
    for (let l = 1; l < CANOPY_MIPS; l++) {
      const src = this._sc('Sh', l - 1);
      this._base(gl, src, l - 1);
      this._out(gl, [[T.glH, l, layer], [this._sc('Sh', l), l]], CANOPY_N >> l);   // o0 = o1
      this._tex(gl, 0, gl.TEXTURE_2D, src);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    for (const tx of [G.Sh, G.Sh1]) this._base(gl, tx, 0);
    }
    if (all || stage === 2) {
    gl.disable(gl.DEPTH_TEST); gl.depthMask(false);
    gl.bindVertexArray(G.vaoFull);
    // 5. shade → Sa0 / Sb0 + pool A0 / B0; the terrain inputs: SHD2 (the
    // frame grids, texel centres exact) or the override
    let sv, sk;
    if (this.sunVisFor) {
      const o = this.sunVisFor(F) || {};
      sv = { tex: o.tex || G.one, xf: o.xf || ONE_XF };
      sk = o.sky && o.sky.tex ? { tex: o.sky.tex, xf: o.sky.xf || ONE_XF } : { tex: G.one, xf: ONE_XF };
    } else {
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1); gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0); gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0); gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false); gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      for (const [tx, a] of [[G.Tv, S.shd.vis], [G.Tk, S.shd.sky]]) {
        this._tex(gl, 0, gl.TEXTURE_2D, tx);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, n, n, gl.RED, gl.UNSIGNED_BYTE, a);
      }
      const xf = [1 / ext, 1 / ext, 0, 0];
      sv = { tex: G.Tv, xf }; sk = { tex: G.Tk, xf };
    }
    this._out(gl, [[G.Sa, 0], [G.Sb, 0], [T.glA, 0, layer], [T.glB, 0, layer]], CANOPY_N);
    gl.useProgram(G.progs.shade);
    this._tex(gl, 0, gl.TEXTURE_2D, G.Gb); gl.uniform1i(this._u(gl, 'shade', 'uG'), 0);
    this._tex(gl, 1, gl.TEXTURE_2D, G.Ds); gl.uniform1i(this._u(gl, 'shade', 'uDs'), 1);
    this._tex(gl, 2, gl.TEXTURE_2D, sv.tex); gl.uniform1i(this._u(gl, 'shade', 'uSv'), 2);
    gl.uniform4fv(this._u(gl, 'shade', 'uSvXf'), sv.xf);
    this._tex(gl, 3, gl.TEXTURE_2D, sk.tex); gl.uniform1i(this._u(gl, 'shade', 'uSk'), 3);
    gl.uniform4fv(this._u(gl, 'shade', 'uSkXf'), sk.xf);
    gl.uniform4f(this._u(gl, 'shade', 'uFr2'), t, n, 0, SPAN_M);
    gl.uniform3fv(this._u(gl, 'shade', 'uSky'), this.light.sky);
    gl.uniform3fv(this._u(gl, 'shade', 'uGnd'), this.light.ground);
    gl.uniform3fv(this._u(gl, 'shade', 'uSun'), this.light.sun);
    gl.uniform3fv(this._u(gl, 'shade', 'uL'), this.L);
    gl.uniform1f(this._u(gl, 'shade', 'uWhite'), this.light.white);
    const L = this.L, lh = Math.hypot(L[0], L[2]);
    gl.uniform3f(this._u(gl, 'shade', 'uSunStep'), lh > 1e-6 ? L[0] / lh : 0, lh > 1e-6 ? L[2] / lh : 0, lh > 1e-6 ? L[1] / lh : 1e6);
    const col = new Float32Array(12), spB = new Float32Array(8);
    S.spc.forEach((s, k) => { col.set(s.col, 3 * k); spB.set([s.opacity, s.catch], 2 * k); });
    gl.uniform3fv(this._u(gl, 'shade', 'uCol'), col);
    gl.uniform2fv(this._u(gl, 'shade', 'uSpB'), spB);
    const taps = aoTaps(t, this.fam.light);
    gl.uniform2fv(this._u(gl, 'shade', 'uAo'), new Float32Array(taps.flat()));
    gl.uniform1fv(this._u(gl, 'shade', 'uAoR'), new Float32Array([Math.hypot(...taps[0]), Math.hypot(...taps[AO_DIRS])]));
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    // 6. A / B mips
    gl.useProgram(G.progs.abmip);
    gl.uniform1i(this._u(gl, 'abmip', 'uA'), 0); gl.uniform1i(this._u(gl, 'abmip', 'uB'), 1); gl.uniform1i(this._u(gl, 'abmip', 'uH'), 2);
    for (let l = 1; l < CANOPY_MIPS; l++) {
      const sa = this._sc('Sa', l - 1), sb = this._sc('Sb', l - 1), sh = this._sc('Sh', l - 1);
      for (const tx of [sa, sb, sh]) this._base(gl, tx, l - 1);
      this._out(gl, [[T.glA, l, layer], [T.glB, l, layer], [this._sc('Sa', l), l], [this._sc('Sb', l), l]], CANOPY_N >> l);   // oA0 = oA1, oB0 = oB1
      this._tex(gl, 0, gl.TEXTURE_2D, sa); this._tex(gl, 1, gl.TEXTURE_2D, sb); this._tex(gl, 2, gl.TEXTURE_2D, sh);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    for (const tx of [G.Sa, G.Sb, G.Sh, G.Sa1, G.Sb1, G.Sh1]) this._base(gl, tx, 0);
    }
    this._out(gl, [], 0);
    gl.bindVertexArray(null);
    if (q) { gl.endQuery(G.timer.TIME_ELAPSED_EXT); this._q.push({ q, stage, seq }); }
    this._poll(gl);
  }

  // the scratch holding mip level l of chain k ('Sh' | 'Sa' | 'Sb'): even levels k, odd k + '1' (MIP-PINGPONG)
  _sc(k, l) { return this.gl[l & 1 ? k + '1' : k]; }

  _tex(gl, unit, target, tex) { gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(target, tex); }

  // a scratch texture read at level l only (no feedback with the level written)
  _base(gl, tex, l) {
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, l);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, l === 0 ? CANOPY_MIPS - 1 : l);
  }

  // fbO's colour attachments: [tex2D, level] | [texArray, level, layer]
  _out(gl, atts, size) {
    const G = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, G.fbO);
    for (let k = 0; k < 4; k++) {
      const a = atts[k], at = gl.COLOR_ATTACHMENT0 + k;
      if (!a) gl.framebufferTexture2D(gl.FRAMEBUFFER, at, gl.TEXTURE_2D, null, 0);
      else if (a.length === 3) gl.framebufferTextureLayer(gl.FRAMEBUFFER, at, a[0], a[1], a[2]);
      else gl.framebufferTexture2D(gl.FRAMEBUFFER, at, gl.TEXTURE_2D, a[0], a[1]);
    }
    if (!atts.length) { gl.bindFramebuffer(gl.FRAMEBUFFER, null); return; }
    gl.drawBuffers(atts.map((_, k) => gl.COLOR_ATTACHMENT0 + k));
    gl.viewport(0, 0, size, size);
  }

  // finished GPU timer queries → gpu_ms (a disjoint frame drops them)
  _poll(gl) {
    const G = this.gl;
    if (!G.timer) return;
    const disjoint = gl.getParameter(G.timer.GPU_DISJOINT_EXT);
    while (this._q.length && gl.getQueryParameter(this._q[0].q, gl.QUERY_RESULT_AVAILABLE)) {
      const { q, stage, seq } = this._q.shift();
      const ms = disjoint ? NaN : gl.getQueryParameter(q, gl.QUERY_RESULT) / 1e6;
      gl.deleteQuery(q);
      if (!disjoint) push(this.stats.gpu_ms[stage], ms);
      // a tile's total: its STAGES' queries summed (a restart's seq never completes)
      const a = this._qSum.get(seq) || { ms: 0, n: 0 };
      a.ms += ms; a.n++;
      if (a.n < STAGES.length) this._qSum.set(seq, a);
      else { this._qSum.delete(seq); if (Number.isFinite(a.ms)) push(this.stats.gpu_tile_ms, a.ms); }
    }
    if (this._qSum.size > 64) for (const k of [...this._qSum.keys()].slice(0, this._qSum.size - 64)) this._qSum.delete(k);
  }

  // ---- dev: regenerate the nearest resident tile and read it back against
  // the CPU reference on the GPU's own ground and the same SHD2 grids
  // (__vr.genCheck, gpuprobe; an override source: the reference sees 1)
  async check(maxTiles = 2) {
    const gl = this.renderer.getContext();
    if (!this.gl || !this.gl.linked || !this.poolTex) return { ok: false, why: NOTHING_YET };
    const res = [];
    const live = this.info.map((v, l) => (v ? { ...v, layer: l } : null)).filter(Boolean)
      .sort((a, b) => a.c - b.c || a.id.localeCompare(b.id)).slice(0, maxTiles);
    for (const v of live) {
      const tile = { id: v.id, c: v.c, i: v.i, j: v.j, packs: packsFor(this.fam.frame(v.c, v.i, v.j, 0), this.crowns, this.fam.reach) };
      const bufs = await Promise.all(flatUrls(this._urls(tile)).map((u) => fetch(transportUrl(u)).then((r) => {
        if (!r.ok) throw new Error(`${u}: HTTP ${r.status}`);
        return r.arrayBuffer();
      })));
      const S = await this._prep(tile, bufs);
      let rb, pre;
      this._epoch++;   // the scratch is overwritten: an in-flight job restarts its stages
      try {
        // what the CONSUMERS see first: the resident layer through the GL
        // objects three binds for the pool textures (the volume's / sprites' uH,
        // uA, uB) — a regenerate-and-read alone passed while they read zeros
        pre = this._readBack(gl, v.layer, this._bound());
        this._gpu(gl, S, v.layer);
        rb = this._readBack(gl, v.layer);
      } finally { this.renderer.resetState(); }
      const F = S.frame, n = F.n, ground = new Float32Array(n * n);
      const f32 = new Float32Array(rb.depth.buffer);
      for (let p = 0; p < n * n; p++) ground[p] = Math.fround(Math.fround(1 - f32[4 * p]) * SPAN_M);
      const ov = !!this.sunVisFor;
      const ref = this.fam.ref(S, ground, this.ctx, this.light, this.L,
        ov ? SUNVIS_ONE : frameVis(S.shd.vis, F), ov ? SUNVIS_ONE : frameVis(S.shd.sky, F));
      let hd = 0, ad = 0;
      for (let l = 0; l < CANOPY_MIPS; l++) for (let p = 0; p < rb.H[l].length; p++) if (pre.H[l][p] !== rb.H[l][p]) hd++;
      for (const k of ['A', 'B']) for (let p = 0; p < rb[k].length; p++) if (Math.abs(pre[k][p] - rb[k][p]) > 1) ad++;
      res.push({ id: v.id, crowns: S.C.n, light: ov ? 'override' : 'shd2', hash: layerHash(rb), ...diffTile(rb, ref),
        bound_same: this._boundSame(), resident_h_diff: hd, resident_ab_diff: ad });
    }
    return { ok: res.length > 0 && res.every((r) => r.h_over1 === 0 && r.mips_bad === 0 && r.bound_same && r.resident_h_diff === 0 && r.resident_ab_diff === 0), tiles: res };
  }

  // the pool written is the pool three binds (null: none yet)
  _boundSame() {
    if (!this.poolTex) return null;
    const T = this.poolTex, bd = this._bound();
    return bd.H === T.glH && bd.A === T.glA && bd.B === T.glB;
  }

  // the GL objects three holds (and binds) for the pool textures now
  _bound() {
    const P = this.renderer.properties, T = this.poolTex;
    return { H: P.get(T.H).__webglTexture, A: P.get(T.A).__webglTexture, B: P.get(T.B).__webglTexture };
  }

  // the pool layer through `src` ({H, A, B} GL objects; default the ones written)
  _readBack(gl, layer, src) {
    const G = this.gl, T = this.poolTex, out = { H: [], A: null, B: null };
    const glH = src ? src.H : T.glH, glAB = { A: src ? src.A : T.glA, B: src ? src.B : T.glB };
    gl.bindFramebuffer(gl.FRAMEBUFFER, G.fbO);
    for (let k = 1; k < 4; k++) gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0 + k, gl.TEXTURE_2D, null, 0);
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    for (let l = 0; l < CANOPY_MIPS; l++) {
      const s = CANOPY_N >> l, u = new Uint32Array(s * s * 4);
      gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, glH, l, layer);
      gl.readPixels(0, 0, s, s, gl.RGBA_INTEGER, gl.UNSIGNED_INT, u);
      const h = new Uint16Array(s * s);
      for (let p = 0; p < s * s; p++) h[p] = u[4 * p];
      out.H.push(h);
    }
    for (const k of ['A', 'B']) {
      const u = new Uint8Array(CANOPY_N * CANOPY_N * 4);
      gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, glAB[k], 0, layer);
      gl.readPixels(0, 0, CANOPY_N, CANOPY_N, gl.RGBA, gl.UNSIGNED_BYTE, u);
      out[k] = u;
    }
    // Dg / Ds float bits through the peek pass into the G-buffer (fbO: Ds
    // is sampled, so never the splat target it is attached to)
    this._out(gl, [[G.Gb, 0]], FRAME_N);
    gl.disable(gl.DEPTH_TEST);
    gl.useProgram(G.progs.peek);
    this._tex(gl, 0, gl.TEXTURE_2D, G.Dg); gl.uniform1i(this._u(gl, 'peek', 'uDg'), 0);
    this._tex(gl, 1, gl.TEXTURE_2D, G.Ds); gl.uniform1i(this._u(gl, 'peek', 'uDs'), 1);
    gl.bindVertexArray(G.vaoFull);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    out.depth = new Uint32Array(FRAME_N * FRAME_N * 4);
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    gl.readPixels(0, 0, FRAME_N, FRAME_N, gl.RGBA_INTEGER, gl.UNSIGNED_INT, out.depth);
    gl.bindVertexArray(null);
    this._out(gl, [], 0);
    return out;
  }

  state() {
    const P = gen.pools.get(this.id);
    return { layers: P ? P.layers : 0, gl_layers: this.poolTex ? this.poolTex.layers : 0, wanted: this._wanted,
      resident: this.info.filter(Boolean).length, broken: this.broken, tiles: this.stats.tiles, crowns: this.stats.crowns,
      nodes: this.stats.nodes, grow: this.stats.grow, cpu_ms: summary(this.stats.cpu_ms), runs: histSummary(this.stats.run_hist, this.stats.run_max),
      gap_max: r2(this.stats.gap_max), gap_at: this.stats.gap_at,
      step_max: Object.fromEntries(Object.entries(this.stats.step_max).map(([k, v]) => [k, r2(v)])),
      gpu_ms: Object.fromEntries(STAGES.map((k, i) => [k, summary(this.stats.gpu_ms[i])])), gpu_tile_ms: summary(this.stats.gpu_tile_ms),
      gpu_est_ms: Object.fromEntries(STAGES.map((k, i) => [k, r2(this._est(i))])),
      lru: { packs: this.packLru.size, shd2: this.shdLru.size, asked: this._asked.size }, light: this.sunVisFor ? 'override' : 'shd2',
      alloc_ms: r2(this.stats.alloc_ms), compile_ms: r2(this.stats.compile_ms), bound: this._boundSame(),
      pool_mb: P ? r2((P.layers * P.bytes) / 1e6) : 0 };
  }
}

// GPU read-back vs the CPU reference: H ≤ 1 quantum, the mips are the max
// of their 2×2 (exact), A / B within bytes, the winner's species
function diffTile(rb, ref) {
  const N = CANOPY_N, H0 = rb.H[0], R0 = ref.H[0];
  let over1 = 0, cov = 0, maxd = 0, crown = 0;
  for (let p = 0; p < N * N; p++) {
    if ((H0[p] === 0) !== (R0[p] === 0)) { cov++; continue; }
    if (!H0[p]) continue;
    crown++;
    const d = Math.abs(H0[p] - R0[p]);
    if (d > maxd) maxd = d;
    if (d > 1) over1++;
  }
  let mipsBad = 0;
  for (let l = 1; l < rb.H.length; l++) {
    const s = N >> l, d = s * 2, P = rb.H[l - 1], M = rb.H[l];
    for (let j = 0; j < s; j++) for (let i = 0; i < s; i++) {
      const a = 2 * j * d + 2 * i;
      if (M[j * s + i] !== Math.max(P[a], P[a + 1], P[a + d], P[a + d + 1])) mipsBad++;
    }
  }
  const enc = (v) => Math.round(255 * (v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055));
  const bytes = (gpu, cpu, srgb) => {
    const ds = [];
    for (let p = 0; p < N * N; p++) {
      if (!H0[p] || !R0[p]) continue;
      let m = 0;
      for (let c = 0; c < 3; c++) m = Math.max(m, Math.abs(gpu[4 * p + c] - (srgb ? enc(cpu[4 * p + c]) : Math.round(255 * cpu[4 * p + c]))));
      ds.push(m);
    }
    ds.sort((a, b) => a - b);
    return { p50: ds.length ? ds[ds.length >> 1] : 0, p99: ds.length ? ds[Math.floor(ds.length * 0.99)] : 0,
      max: ds.length ? ds[ds.length - 1] : 0, over2: ds.filter((v) => v > 2).length };
  };
  let spBad = 0;
  for (let p = 0; p < N * N; p++) if (H0[p] && R0[p] && rb.B[4 * p + 3] !== Math.round(255 * ref.B[4 * p + 3])) spBad++;
  return { texels: crown, h_over1: over1, h_max_q: maxd, coverage_diff: cov, mips_bad: mipsBad, species_diff: spBad,
    A: bytes(rb.A, ref.A, true), B: bytes(rb.B, ref.B, false) };
}

// a read-back layer's FNV-1a 32 (H every level, A, B): the same tile generated twice hashes the same
function layerHash(rb) {
  let h = 0x811c9dc5;
  const eat = (u8) => { for (let p = 0; p < u8.length; p++) h = Math.imul(h ^ u8[p], 0x01000193); };
  for (const a of rb.H) eat(new Uint8Array(a.buffer, a.byteOffset, a.byteLength));
  eat(rb.A); eat(rb.B);
  return (h >>> 0).toString(16).padStart(8, '0');
}
function push(a, v) { a.push(v); if (a.length > 256) a.shift(); }
// the run histogram (0.1 ms bins) → {n, p50, p99, max} (a bin's upper edge; max exact)
function histSummary(h, max) {
  let n = 0;
  for (const c of h) n += c;
  if (!n) return null;
  const at = (q) => { let s = 0; for (let b = 0; b < h.length; b++) { s += h[b]; if (s >= q * n) return (b + 1) / 10; } return max; };
  return { n, p50: r2(at(0.5)), p99: r2(at(0.99)), max: r2(max) };
}
const r2 = (v) => Math.round(v * 100) / 100;
function summary(a) {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  return { n: s.length, median: r2(s[s.length >> 1]), p95: r2(s[Math.floor(s.length * 0.95)]), max: r2(s[s.length - 1]) };
}

// the decoded inputs: two gensched InputCaches (LRU, the queued tiles' inputs pinned)
const isPack = (u) => u.endsWith('.cpk');
const lruOf = (G, u) => (isPack(u) ? G.packLru : G.shdLru);
const flatUrls = (U) => [...U.packs, ...U.vis.map((v) => v[2]), ...U.sky.map((v) => v[2])];
// python f'{cell:g}' (bake._ship_shd2's level dir) for the SHD2 cells
const cellG = (v) => String(Number(v.toPrecision(6)));

// the SHD2 tiles [gx, gz] a frame reads: its texels (x0 / t .. + n − 1, the
// level texel = the frame texel) clamped to the level [0, w) × [0, h)
function shdSpan(lv, F, tp) {
  const i0 = Math.round(F.x0 / F.t), j0 = Math.round(F.z0 / F.t), out = [];
  const g = (a, w) => Math.floor(Math.min(w - 1, Math.max(0, a)) / tp);
  for (let gz = g(j0, lv.h); gz <= g(j0 + F.n - 1, lv.h); gz++) for (let gx = g(i0, lv.w); gx <= g(i0 + F.n - 1, lv.w); gx++) out.push([gx, gz]);
  return out;
}
// the frame's n² u8 grid of one SHD2 level from its tiles (T: 'gx_gz' →
// Uint8Array tp²), out-of-level indices clamped to the edge texel
function* shdFrameSteps(lv, F, tp, T) {
  const n = F.n, a = new Uint8Array(n * n), i0 = Math.round(F.x0 / F.t), j0 = Math.round(F.z0 / F.t);
  const col = new Int32Array(n), cgx = new Int32Array(n);
  for (let q = 0; q < n; q++) { const gi = Math.min(lv.w - 1, Math.max(0, i0 + q)); cgx[q] = Math.floor(gi / tp); col[q] = gi - cgx[q] * tp; }
  for (let b = 0; b < n; b++) {
    if (b && b % STEP_ROWS === 0) yield;
    const gj = Math.min(lv.h - 1, Math.max(0, j0 + b)), gz = Math.floor(gj / tp), row = (gj - gz * tp) * tp;
    let gx = -1, src = null;
    for (let q = 0; q < n; q++) {
      if (cgx[q] !== gx) { gx = cgx[q]; src = T.get(`${gx}_${gz}`); if (!src) throw new Error(`canopy: SHD2 tile ${gx}_${gz} missing`); }
      a[b * n + q] = src[row + col[q]];
    }
  }
  return a;
}
// the splat's per-crown instance arrays (stage 1's two buffers): fc = lx, lz,
// h, r (f32 ×4), ic = hash, species (u32 ×2); yields every STEP_RECORDS crowns
function* instArrays(C) {
  const fc = new Float32Array(4 * C.n), ic = new Uint32Array(2 * C.n);
  for (let k = 0; k < C.n; k++) {
    if (k && k % STEP_RECORDS === 0) yield;
    fc[4 * k] = C.lx[k]; fc[4 * k + 1] = C.lz[k]; fc[4 * k + 2] = C.h[k]; fc[4 * k + 3] = C.r[k];
    ic[2 * k] = C.hash[k]; ic[2 * k + 1] = C.sp[k];
  }
  return { fc, ic };
}
// a grey WebP tile (ArrayBuffer) → Uint8Array tp² (R): the pool's canopy.grey
// (pool-forest.js: the decode, the canvas read and the R pick in a worker —
// 4.1–5.5 s of main-thread self time here before, gs-canopygen-slice)
const decodeGrey = (buf, tp) => poolRun('canopy.grey', { buf, tp }, { prio: 'near' });

// ---------------------------------------------------------------- module API
let C = null, S = null;   // the tree family, the shrub family

// main.js, once the ground tree exists (opt {renderer, meta, sea: the world's
// SEA1, baseUrl, tree}): nothing without meta.crowns (a bake
// before the crown pack) — the pass is not registered, nothing generates.
// The shrub family besides, only with meta.crowns.shrubs (CPK1); its own
// failure (e.g. no SHD2 level at 1 m) is logged and never takes the trees down
export function installCanopy(opt) {
  if (C || !opt.meta || !opt.meta.crowns || !opt.tree) return null;
  if (opt.meta.crowns.layout !== 'CPK1') throw new Error(`canopy: meta.crowns.layout ${opt.meta.crowns.layout}`);
  C = new CanopyGen(opt);
  const sh = opt.meta.crowns.shrubs;
  if (sh) {
    try {
      if (sh.layout !== 'CPK1') throw new Error(`canopy shrub: meta.crowns.shrubs.layout ${sh.layout}`);
      S = new CanopyGen(opt, FAM.shrub);
    } catch (e) { S = null; console.error('[canopy shrub]', e.message); }
  }
  return C;
}
// once a tick (main.js, before genFrame): the wanted tiles at the eye (both families)
export function canopyTick(pos, now) { if (C) C.tick(pos, now); if (S) S.tick(pos, now); }
// the terrain-light override (both families): fn(frame) → {tex: a
// WebGLTexture (R = visibility) | null (1), xf: [sx, sz, ox, oz] — uv =
// frame m × (sx, sz) + (ox, oz), sky?: {tex, xf} (absent: 1)}; null = SHD2
// (the default)
export function setSunVisSource(fn) { if (C) C.sunVisFor = fn || null; if (S) S.sunVisFor = fn || null; }
// consumers (gs-forestvol): the pool's three textures + what a layer holds
export function canopyTextures() { return C && C.poolTex ? { A: C.poolTex.A, B: C.poolTex.B, H: C.poolTex.H } : null; }
export function canopySlot(id) { const l = gen.slotOf(FAM.tree.id, id); return l >= 0 && C ? { layer: l, ...C.info[l] } : null; }
export function canopyTiles() { return C ? C.info.map((v, l) => (v ? { layer: l, ...v } : null)).filter(Boolean) : []; }
// the dev state (__vr.gen().canopy): the tree family's + `shrub` (the shrub family's, null without)
export function canopyState() { return C ? { ...C.state(), shrub: S ? S.state() : null } : null; }
// __vr.genCheck(n) / gpuprobe gen_check: the tree tiles as before; with a
// shrub family holding tiles, `shrub` = its own check (the same diff against
// shrubRef) and ok = both (a shrub family with nothing resident is reported,
// not failed: phones / software keep it off)
export async function canopyCheck(n) {
  if (!C) return null;
  const r = await C.check(n);
  if (S) {
    const s = await S.check(n);
    r.shrub = s;
    if (s.why !== NOTHING_YET) r.ok = r.ok && s.ok;
  }
  return r;
}
// the shrub family (gs-family-shrub; shrubtap.js reads it): its pool's
// textures, a tile's slot, the resident tiles, the dev state, the world
// (size + meta.tex.light + the sun direction)
export function shrubTextures() { return S && S.poolTex ? { A: S.poolTex.A, B: S.poolTex.B, H: S.poolTex.H } : null; }
export function shrubSlot(id) { const l = gen.slotOf(FAM.shrub.id, id); return l >= 0 && S ? { layer: l, ...S.info[l] } : null; }
export function shrubTiles() { return S ? S.info.map((v, l) => (v ? { layer: l, ...v } : null)).filter(Boolean) : []; }
export function shrubState() { return S ? S.state() : null; }
export function shrubWorld() { return S && S.world ? { ...S.world, light: S.light, L: S.L } : null; }
// the GLSL of a family's programs ('tree' | 'shrub'; check-forest-shrub reads it by source)
export function familyPrograms(key) { return programs(FAM[key]); }
// a family's live view → {cap, lo, hi, range, vm} (check-forest-shrub sweeps it)
export function familyView(key, live) { return FAM[key].live(live); }
